import React from 'react'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import {
  addItem,
  clearCart,
  hideItemEditor,
  selectCartHasItems,
  selectCartItemsProperty,
  selectIsItemEditorOpen,
  selectItemEditorMode,
  selectItemToEdit,
  updateItem,
  updateItemEditor
} from '../../store/slices/shoppingCartSlice'
import ShoppingCartServiceItem from './ShoppingCartServiceItem'
import * as types from '../../types'
import {AppView, ServiceOffer} from '../../types'
import ShoppingCartItem from './ShoppingCartItem'
import {setAppView} from '../../store/slices/appViewSlice'
import {addToastNotification} from '../../store/slices/toastNotificationSlice'
import {generateUUID} from '../../services/utils'
import {selectSearchParams} from '../../store/slices/searchParamsSlice'
import {useSearchServiceOffersQuery} from '../../services/ibeApi'
import {getLogger} from '../../services/logging'
import Alert from '../common/Alert'
import {FocusTrap} from 'focus-trap-react'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('ShoppingCartItemEditorModal')

const ShoppingCartItemEditorModal = () => {
  const dispatch = useAppDispatch()
  const isOpen = useAppSelector(selectIsItemEditorOpen)
  const item = useAppSelector(selectItemToEdit) as types.ShoppingCartItem
  const editorMode = useAppSelector(selectItemEditorMode)
  const search = useAppSelector(selectSearchParams)
  const cartHasItems = useAppSelector(selectCartHasItems)
  const cartItemsPropertyId = useAppSelector(selectCartItemsProperty)
  const isMultiPropertyViolation =
    cartHasItems && cartItemsPropertyId !== item?.propertyId
  const ratePlanId = item?.offer?.ratePlan?.id || ''
  const serviceOffersQuery = useSearchServiceOffersQuery(
    {
      ratePlanId,
      guests: item?.guests || 1,
      dateRange: search.dateRange
    },
    {skip: !ratePlanId}
  )
  const isLoading = serviceOffersQuery.isLoading
  const maxGuests = item?.offer?.unitGroup?.maxPersons || item?.guests || 10
  const serviceOffers: ServiceOffer[] = serviceOffersQuery.data?.services || []
  const additionalServiceIds = item?.additionalServices
    ? item.additionalServices.map(({service}) => service.id)
    : []

  if (!item) {
    return null
  }

  return (
    <>
      {isOpen && (
        <FocusTrap active={isOpen}>
          <div
            className="modal modal-open"
            onClick={e => {
              if (e.target === e.currentTarget) {
                dispatch(hideItemEditor())
              }
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-box w-[95%] max-w-screen-md max-h-[95%] px-4 md:px-6">
              <h1 className="text-lg font-bold">
                {editorMode === 'Add' ? 'Add room' : 'Edit room details'}
              </h1>
              <button
                className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4"
                onClick={() => {
                  dispatch(hideItemEditor())
                }}
                aria-label="Close shopping cart item."
              >
                ✕
              </button>

              {isMultiPropertyViolation && (
                <Alert type="error" className="my-4">
                  <p>
                    You cannot add rooms from multiple properties to the
                    shopping cart. If you would like to book rooms from multiple
                    properties, you will need to complete the booking process
                    for each property individually. You may also clear existing
                    items in the shopping cart to continue adding this room.
                  </p>
                  <button
                    className="btn mt-4"
                    onClick={() => {
                      logger.info({
                        message:
                          'Clearing shopping cart to add room from a different property...',
                        data: {item, cartItemsPropertyId}
                      })
                      pushDataLayerEvent({
                        event: 'Shopping_Cart_Clear',
                        category: 'IBE',
                        value: 1
                      })
                      dispatch(clearCart())
                    }}
                    aria-label="Clear shopping cart."
                  >
                    Clear shopping cart
                  </button>
                </Alert>
              )}

              {/* TODO: enable this if we have services that are charged per guest */}
              {/* Guests */}
              {/* <h2 className="font-semibold text-sm mt-2 mb-1">
                Choose Number of Guests:
              </h2>
              <div className="card card-compact border bg-white shadow w-full">
                <div className="flex flex-row items-center px-4 py-3">
                  <select
                    style={{
                      appearance: 'none'
                    }}
                    className={clsx(
                      'text-sm font-medium cursor-pointer bg-white',
                      'w-full py-3.5',
                      'xl:py-1',
                      'border-0 focus:border-0 focus:ring-0 outline-none'
                    )}
                    value={item.guests}
                    onChange={e => {
                      const guests = parseInt(e.target.value, 10)

                      pushDataLayerEvent({
                        event: 'Shopping_Cart_Change_Guests',
                        category: 'IBE',
                        value: 1
                      })

                      logger.info({
                        message: 'Guests updated',
                        data: {guests}
                      })

                      const updatedItem = {
                        ...item,
                        guests: guests
                      }
                      dispatch(updateItemEditor(updatedItem))
                    }}
                    tabIndex={0}
                    aria-label="Select the number of guests."
                    aria-required="true"
                  >
                    {Array.from({length: maxGuests}, (_, i) => i + 1).map(
                      (guestCount: number) => {
                        return (
                          <option key={guestCount} value={guestCount}>
                            {guestCount} {guestCount > 1 ? 'guests' : 'guest'}
                          </option>
                        )
                      }
                    )}
                  </select>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="ml-1 flex-0 text-2xs"
                    title="Guest field dropdown"
                  />
                </div>
              </div> */}

              {/* Additional Services */}

              {isLoading && (
                <div className="flex flex-col items-center justify-center h-48">
                  <span className="loading loading-spinner"></span>
                  <h2>Loading services...</h2>
                </div>
              )}

              {!isLoading && serviceOffers.length > 0 && (
                <>
                  <h2 className="font-semibold text-sm mt-2 mb-1">
                    Choose Additional Services:
                  </h2>
                  <div className="flex flex-col gap-2">
                    {serviceOffers.map(
                      (serviceOffer: ServiceOffer, index: number) => {
                        return (
                          <ShoppingCartServiceItem
                            key={index}
                            serviceOffer={serviceOffer}
                            isSelected={additionalServiceIds.includes(
                              serviceOffer.service.id
                            )}
                            onSelectionChange={(isSelected: boolean) => {
                              logger.info({
                                message: 'Updated service selection...',
                                data: {isSelected, serviceOffer}
                              })
                              const updatedItem = {
                                ...item,
                                additionalServices: isSelected
                                  ? [...item.additionalServices, serviceOffer]
                                  : item.additionalServices.filter(service => {
                                      return (
                                        service.service.id !==
                                        serviceOffer.service.id
                                      )
                                    })
                              }

                              dispatch(updateItemEditor(updatedItem))
                            }}
                          />
                        )
                      }
                    )}
                  </div>
                </>
              )}

              <div className="card card-compact border bg-white shadow w-full mt-4">
                <div className="card-body">
                  <ShoppingCartItem
                    item={item}
                    showActions={false}
                    showItemIndex={false}
                  />
                </div>
              </div>

              <div>
                <div className="modal-action">
                  {editorMode === 'Add' ? (
                    <>
                      <button
                        className="flex flex-col items-center gap-0 btn btn-ghost"
                        disabled={isLoading || isMultiPropertyViolation}
                        onClick={() => {
                          logger.info({
                            message: 'Added room to cart.',
                            data: {item}
                          })
                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Add',
                            category: 'IBE',
                            value: 1
                          })
                          dispatch(addItem(item))
                          dispatch(hideItemEditor())
                          // show toast notification
                          dispatch(
                            addToastNotification({
                              id: generateUUID(),
                              type: 'add',
                              message:
                                'Room successfully added to your shopping cart.'
                            })
                          )
                        }}
                        aria-label="Add room to shopping cart and continue shopping."
                      >
                        Add Room
                      </button>

                      <button
                        className="btn btn-secondary"
                        disabled={isLoading || isMultiPropertyViolation}
                        onClick={() => {
                          logger.info({
                            message:
                              'Added room to cart, continuing to checkout.',
                            data: {item}
                          })

                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Add_Checkout',
                            category: 'IBE',
                            value: 1
                          })

                          dispatch(addItem(item))
                          dispatch(hideItemEditor())
                          dispatch(setAppView(AppView.CHECKOUT))
                        }}
                        aria-label="Add room to shopping cart and checkout."
                      >
                        Add Room & Checkout
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn btn-ghost"
                        onClick={() => {
                          logger.info({
                            message: 'Cancelled edits to room item...',
                            data: {item}
                          })

                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Cancel_Edit',
                            category: 'IBE',
                            value: 1
                          })

                          dispatch(hideItemEditor())
                        }}
                        aria-label="Cancel edits to shopping cart item."
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={isLoading || isMultiPropertyViolation}
                        onClick={() => {
                          logger.info({
                            message: 'Saved edits to room item...',
                            data: {item}
                          })
                          pushDataLayerEvent({
                            event: 'Shopping_Cart_Save_Edit',
                            category: 'IBE',
                            value: 1
                          })
                          dispatch(updateItem(item))
                          dispatch(hideItemEditor())
                        }}
                        aria-label="Save edits to shopping cart item."
                      >
                        Save
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </FocusTrap>
      )}
    </>
  )
}

export default ShoppingCartItemEditorModal
